import React, { useState, useEffect } from 'react';
import { apiBase, resolveOrgId } from './utils/authHeaders';
import { Calendar, Users, DollarSign, Clock, MapPin, CheckCircle, AlertCircle, Plus, Edit, Trash2, Star } from 'lucide-react';

const EnhancedEventPlanning = () => {
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid, calendar, list
  const [filterStatus, setFilterStatus] = useState('all');

  const API_URL = apiBase();

  useEffect(() => {
    fetchEvents();
    fetchVenues();
    fetchTemplates();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = resolveOrgId();
      
      const response = await fetch(`${API_URL}/api/events/enhanced`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVenues = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = resolveOrgId();
      
      const response = await fetch(`${API_URL}/api/venues`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVenues(data);
      }
    } catch (error) {
      console.error('Error fetching venues:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const orgId = resolveOrgId();
      
      const response = await fetch(`${API_URL}/api/event-templates`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const getEventStatusColor = (status) => {
    const colors = {
      inquiry: 'text-gray-600 bg-gray-100',
      proposed: 'text-blue-600 bg-blue-100',
      booked: 'text-purple-600 bg-purple-100',
      planning: 'text-yellow-600 bg-yellow-100',
      confirmed: 'text-green-600 bg-green-100',
      in_progress: 'text-orange-600 bg-orange-100',
      completed: 'text-teal-600 bg-teal-100',
      cancelled: 'text-red-600 bg-red-100',
      postponed: 'text-indigo-600 bg-indigo-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      corporate: <Users className="w-4 h-4" />,
      wedding: <Star className="w-4 h-4" />,
      birthday: <Star className="w-4 h-4" />,
      conference: <Users className="w-4 h-4" />,
      meeting: <Users className="w-4 h-4" />
    };
    return icons[type] || <Calendar className="w-4 h-4" />;
  };

  const filteredEvents = events.filter(event => 
    filterStatus === 'all' || event.event_status === filterStatus
  );

  const upcomingEvents = events.filter(event => 
    new Date(event.event_date) > new Date() && !['cancelled', 'completed'].includes(event.event_status)
  );
  const todayRevenue = events.reduce((sum, event) => 
    sum + (event.final_payment_paid ? event.actual_cost : 0), 0
  );
  const pendingProposals = events.filter(event => event.event_status === 'proposed').length;

  const EventCard = ({ event }) => (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
         onClick={() => { setSelectedEvent(event); }}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-2">
          {getEventTypeIcon(event.event_type)}
          <h3 className="text-lg font-semibold">{event.event_name}</h3>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEventStatusColor(event.event_status)}`}>
          {event.event_status.replace('_', ' ')}
        </span>
      </div>
      
      <div className="space-y-2 text-sm text-gray-600">
        <div className="flex justify-between">
          <span className="flex items-center">
            <Calendar className="w-4 h-4 mr-1" />
            {new Date(event.event_date).toLocaleDateString()}
          </span>
          <span className="flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            {new Date(event.start_time).toLocaleTimeString()}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="flex items-center">
            <Users className="w-4 h-4 mr-1" />
            {event.expected_guests} guests
          </span>
          <span className="flex items-center">
            <MapPin className="w-4 h-4 mr-1" />
            {event.venue_type}
          </span>
        </div>

        <div className="flex justify-between">
          <span>Client:</span>
          <span className="font-medium">{event.client_name}</span>
        </div>

        <div className="flex justify-between">
          <span>Budget:</span>
          <span className="font-medium">${event.budget?.toFixed(2) || '0.00'}</span>
        </div>

        <div className="flex justify-between">
          <span>Est. Cost:</span>
          <span className="font-medium">${event.estimated_cost.toFixed(2)}</span>
        </div>
      </div>

      {event.special_requirements.length > 0 && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-1">
            {event.special_requirements.slice(0, 2).map((req, index) => (
              <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {req}
              </span>
            ))}
            {event.special_requirements.length > 2 && (
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                +{event.special_requirements.length - 2} more
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-4 pt-4 border-t">
        <div className="flex items-center space-x-2">
          {event.deposit_paid ? (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
              Deposit Paid
            </span>
          ) : (
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
              Deposit Pending
            </span>
          )}
        </div>
        <div className="flex space-x-1">
          <button 
            onClick={(e) => { e.stopPropagation(); /* Edit event */ }}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <Edit className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const EventModal = () => {
    if (!selectedEvent) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold">{selectedEvent.event_name}</h2>
            <button 
              onClick={() => setSelectedEvent(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-4">Event Details</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Type:</span>
                  <p className="font-medium capitalize">{selectedEvent.event_type}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Status:</span>
                  <p>
                    <span className={`px-2 py-1 rounded-full text-xs ${getEventStatusColor(selectedEvent.event_status)}`}>
                      {selectedEvent.event_status.replace('_', ' ')}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Date & Time:</span>
                  <p className="font-medium">
                    {new Date(selectedEvent.event_date).toLocaleDateString()} at {new Date(selectedEvent.start_time).toLocaleTimeString()}
                  </p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Guests:</span>
                  <p className="font-medium">{selectedEvent.expected_guests} expected</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Venue:</span>
                  <p className="font-medium capitalize">{selectedEvent.venue_type}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Client Information</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Name:</span>
                  <p className="font-medium">{selectedEvent.client_name}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Contact:</span>
                  <p className="font-medium">{selectedEvent.contact_email}</p>
                  <p className="font-medium">{selectedEvent.contact_phone}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Address:</span>
                  <p className="font-medium">{selectedEvent.delivery_address}</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Financial Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Budget:</span>
                  <span className="font-medium">${selectedEvent.budget?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Estimated Cost:</span>
                  <span className="font-medium">${selectedEvent.estimated_cost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Deposit:</span>
                  <span className="font-medium">${selectedEvent.deposit_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Deposit Status:</span>
                  <span className={`font-medium ${selectedEvent.deposit_paid ? 'text-green-600' : 'text-yellow-600'}`}>
                    {selectedEvent.deposit_paid ? 'Paid' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Requirements</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Catering:</span>
                  <p className="font-medium capitalize">{selectedEvent.catering_type}</p>
                </div>
                {selectedEvent.special_requirements.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-600">Special Requirements:</span>
                    <ul className="mt-1">
                      {selectedEvent.special_requirements.map((req, index) => (
                        <li key={index} className="text-sm">• {req}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedEvent.dietary_restrictions.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-600">Dietary Restrictions:</span>
                    <ul className="mt-1">
                      {selectedEvent.dietary_restrictions.map((restriction, index) => (
                        <li key={index} className="text-sm">• {restriction}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
            <button 
              onClick={() => setSelectedEvent(null)}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Edit Event
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Event Planning</h1>
        <div className="flex space-x-2">
          <button 
            onClick={() => setViewMode('grid')}
            className={`px-4 py-2 rounded-md ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Grid
          </button>
          <button 
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 rounded-md ${viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Calendar
          </button>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>New Event</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-full">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Upcoming Events</p>
              <p className="text-2xl font-semibold">{upcomingEvents.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-full">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Pending Proposals</p>
              <p className="text-2xl font-semibold">{pendingProposals}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Today's Revenue</p>
              <p className="text-2xl font-semibold">${todayRevenue.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-full">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Guests</p>
              <p className="text-2xl font-semibold">
                {events.reduce((sum, event) => sum + event.expected_guests, 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
          <select aria-label="Filter by Status" 
            className="border border-gray-300 rounded-md px-3 py-2"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Events</option>
            <option value="inquiry">Inquiry</option>
            <option value="proposed">Proposed</option>
            <option value="booked">Booked</option>
            <option value="planning">Planning</option>
            <option value="confirmed">Confirmed</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEvents.map(event => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>

      {selectedEvent && <EventModal />}
    </div>
  );
};

export default EnhancedEventPlanning;
